// GpuPerformancePanel.tsx — the shared GPU performance dashboard.
//
// This component lives in the inference web tree because inference owns
// the GPU fleet, but it's deliberately self-contained (no app-specific
// imports — only react/react-bootstrap/react-icons/recharts) so thirdeye
// can import it directly and render the identical panel. Each app wires
// its own data via the `source` prop (different backends, same chart).
//
// Layout:
//   1. Three small metric cards (1m rolling): power, tok/s, GPU util.
//   2. GPU Performance History area chart.
//
// The "1m" windows are computed client-side from a ring buffer of the
// last 12 stats samples (polled every `pollMs`, default 5s).
import { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { BsLightningCharge, BsSpeedometer2, BsCpu, BsActivity } from 'react-icons/bs';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';

// ───────────────────────────────────────────────────────────────────────
// Data contract
// ───────────────────────────────────────────────────────────────────────

// GpuStatsSummary is the cluster-wide live reading driving the three
// metric cards. Both backends already expose these fields on /api/stats's
// `gpu` block.
export interface GpuStatsSummary {
  power_watts: number;
  tokens_per_sec: number;
  utilization_percent: number;
}

// GpuHistorySample is one per-GPU reading in the history series. Field
// names match both inference's /api/gpu/history and thirdeye's
// store.GPUMetric so either source feeds the chart unchanged.
export interface GpuHistorySample {
  timestamp: string;
  gpu_name: string;
  temperature: number;
  power_draw: number;
}

// GpuMetricsSource is the per-app data adapter. `stats` returns the live
// summary (or null when unavailable); `history` returns the time series
// for the requested window.
export interface GpuMetricsSource {
  stats(): Promise<GpuStatsSummary | null>;
  history(hours: number): Promise<GpuHistorySample[]>;
}

export interface GpuPerformancePanelProps {
  source: GpuMetricsSource;
  // Window for the history chart in hours (default 24).
  historyHours?: number;
  // Poll cadence in ms (default 5000).
  pollMs?: number;
}

// ───────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────

// average over a numeric ring buffer; returns 0 when empty so the UI
// doesn't have to guard.
function avg(xs: number[]): number {
  if (xs.length === 0) return 0;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

// pushRing appends x and trims to N items, returning a new array.
function pushRing(buf: number[], x: number, n: number): number[] {
  const next = buf.length >= n ? [...buf.slice(buf.length - n + 1), x] : [...buf, x];
  return next;
}

const RING_N = 12; // 12 samples × 5s = 60s window

// ───────────────────────────────────────────────────────────────────────
// Panel
// ───────────────────────────────────────────────────────────────────────

export function GpuPerformancePanel({
  source,
  historyHours = 24,
  pollMs = 5000,
}: GpuPerformancePanelProps) {
  // Metrics ring buffers.
  const [powerRing, setPowerRing] = useState<number[]>([]);
  const [tokRing, setTokRing] = useState<number[]>([]);
  const [utilRing, setUtilRing] = useState<number[]>([]);

  // GPU history (area chart).
  const [gpuHistory, setGpuHistory] = useState<GpuHistorySample[]>([]);

  // Initial load + poll loop. `source` is expected to be stable (memoized
  // by the parent); a new identity restarts the loop.
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const [stats, history] = await Promise.all([
          source.stats().catch(() => null),
          source.history(historyHours).catch(() => [] as GpuHistorySample[]),
        ]);
        if (!alive) return;
        if (stats) {
          setPowerRing((r) => pushRing(r, stats.power_watts ?? 0, RING_N));
          setTokRing((r) => pushRing(r, stats.tokens_per_sec ?? 0, RING_N));
          // Cluster-averaged compute utilization (mean of nvidia-smi
          // utilization.gpu across every GPU scraped this tick), not an
          // allocation ratio.
          setUtilRing((r) => pushRing(r, stats.utilization_percent ?? 0, RING_N));
        }
        setGpuHistory(history);
      } catch { /* swallow — next tick retries */ }
    };
    tick();
    const id = setInterval(tick, pollMs);
    return () => { alive = false; clearInterval(id); };
  }, [source, historyHours, pollMs]);

  // Pivot the per-GPU history rows by timestamp. Each tick the collector
  // emits one row per physical GPU sampled (keyed by gpu_name =
  // "<friendly> #<uuid-suffix>"); recharts wants one row per timestamp
  // with each series as a flat field. When the cluster has ≤ MAX_GPU_LINES
  // GPUs we render a line per device for both power and temperature;
  // beyond that we collapse to a sum-of-power + avg-of-temperature line so
  // the legend stays readable.
  const MAX_GPU_LINES = 6;
  const { chartData, gpuNames, summed } = useMemo(() => {
    const names = Array.from(new Set(gpuHistory.map((e) => e.gpu_name))).sort();
    const sum = names.length > MAX_GPU_LINES;
    const byTs = new Map<string, GpuHistorySample[]>();
    for (const e of gpuHistory) {
      const arr = byTs.get(e.timestamp) ?? [];
      arr.push(e);
      byTs.set(e.timestamp, arr);
    }
    const ts = Array.from(byTs.keys()).sort();
    const rows = ts.map((t) => {
      const samples = byTs.get(t)!;
      if (sum) {
        const power_sum = samples.reduce((a, r) => a + (r.power_draw ?? 0), 0);
        const temp_avg = samples.length
          ? samples.reduce((a, r) => a + (r.temperature ?? 0), 0) / samples.length
          : 0;
        return { timestamp: t, power_sum, temp_avg } as Record<string, number | string>;
      }
      const out: Record<string, number | string> = { timestamp: t };
      for (const r of samples) {
        out[`power::${r.gpu_name}`] = r.power_draw;
        out[`temp::${r.gpu_name}`] = r.temperature;
      }
      return out;
    });
    return { chartData: rows, gpuNames: names, summed: sum };
  }, [gpuHistory]);
  // Stable per-GPU hues so a given device keeps its color across renders.
  const POWER_HUES = [199, 162, 35, 290, 5, 105];
  const TEMP_HUES  = [25,  340, 75,  220, 250, 130];

  const power = useMemo(() => avg(powerRing), [powerRing]);
  const tok = useMemo(() => avg(tokRing), [tokRing]);
  const util = useMemo(() => avg(utilRing), [utilRing]);

  return (
    <div>
      {/* Top: three 1m-window metric cards */}
      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="h-100">
            <Card.Header className="d-flex align-items-center gap-2">
              <BsLightningCharge className="text-warning" />
              <span>Power Usage</span>
            </Card.Header>
            <Card.Body className="d-flex flex-column justify-content-center align-items-center">
              <h1 className="display-3">{Math.round(power)}W</h1>
              <Card.Text className="text-muted">1m avg</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100">
            <Card.Header className="d-flex align-items-center gap-2">
              <BsSpeedometer2 className="text-primary" />
              <span>Throughput</span>
            </Card.Header>
            <Card.Body className="d-flex flex-column justify-content-center align-items-center">
              <h1 className="display-3">{tok.toFixed(1)} tok/s</h1>
              <Card.Text className="text-muted">1m avg</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100">
            <Card.Header className="d-flex align-items-center gap-2">
              <BsCpu className="text-info" />
              <span>GPU Utilization</span>
            </Card.Header>
            <Card.Body className="d-flex flex-column justify-content-center align-items-center">
              <h1 className="display-3">{util.toFixed(0)}%</h1>
              <Card.Text className="text-muted">1m avg</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* GPU Performance History */}
      <Card style={{ height: '350px' }}>
        <Card.Header className="d-flex align-items-center gap-2 bg-transparent">
          <BsActivity className="text-info" />
          <span>GPU Performance History ({historyHours}h)</span>
        </Card.Header>
        <Card.Body>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis
                dataKey="timestamp"
                tickFormatter={(val) => new Date(val).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                stroke="#666"
                fontSize={10}
              />
              <YAxis stroke="#666" fontSize={10} />
              <Tooltip
                contentStyle={{ backgroundColor: '#222', border: '1px solid #444' }}
                labelFormatter={(val) => new Date(val).toLocaleString()}
              />
              <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
              {summed ? (
                <>
                  <Area type="monotone" dataKey="power_sum" name="Power total (W)" stroke="#17a2b8" fill="transparent" isAnimationActive={false} />
                  <Area type="monotone" dataKey="temp_avg"  name="Temp avg (°C)"   stroke="#fd7e14" fill="transparent" isAnimationActive={false} />
                </>
              ) : (
                gpuNames.flatMap((g, i) => [
                  <Area
                    key={`p::${g}`}
                    type="monotone"
                    dataKey={`power::${g}`}
                    name={`${g} power (W)`}
                    stroke={`hsl(${POWER_HUES[i % POWER_HUES.length]} 70% 60%)`}
                    fill="transparent"
                    isAnimationActive={false}
                  />,
                  <Area
                    key={`t::${g}`}
                    type="monotone"
                    dataKey={`temp::${g}`}
                    name={`${g} temp (°C)`}
                    stroke={`hsl(${TEMP_HUES[i % TEMP_HUES.length]} 70% 60%)`}
                    strokeDasharray="3 3"
                    fill="transparent"
                    isAnimationActive={false}
                  />,
                ])
              )}
            </AreaChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>
    </div>
  );
}

export default GpuPerformancePanel;
